import React, { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Flex, Text } from 'rebass';
import { useHistory } from 'react-router-dom';
import useMode from '../../hooks/useMode';
import { useStoreFilters } from '../../hooks/useStoredFilters';
import { TestReportFilters } from '@/modes/waterSupplier/pages/testReports/FilterBarTestReport';
import { gql } from '@/modes/waterSupplier/gql';

const APPROVAL_QUERY = gql(`
  query TestResults($where: JSON, $limit: Int = 10) {
    initialResultByTester: backflowTests(
      where: $where
      groupBy: [
        "testingCompanyId"
        "testingCompanyName"
        "testerName"
        "testerId"
        "initialResult"
      ]
      aggregate: [{ col: "jobId", type: DISTINCT, as: "backflowTests" }]
      orderBy: [{ col: "agg.backflowTests", dir: DESC }]
      limit: $limit
    ) {
      testingCompanyId
      testingCompanyName
      testerId
      testerName
      initialResult
      agg
    }
  }
`);

type TopCompaniesData = {
  initialResultByTester: {
    testingCompanyName: string;
    testingCompanyId: string;
    testerId: string;
    testerName: string;
    initialResult: 'PASS' | 'FAIL';
    agg: {
      backflowTests: number;
    };
  }[];
};

type InitialResultByTesterWidgetProps = {
  width: number;
  height: number;
};

type TesterGrouping = {
  testerId: string;
  testerName: string;
  tests: {
    TOTAL: number;
    PASS: number;
    FAIL: number;
  };
};

type CompanyGrouping = {
  companyId: string;
  companyName: string;
  tests: {
    TOTAL: number;
    PASS: number;
    FAIL: number;
  };
  testers: Record<string, TesterGrouping>;
};

const InitialResultByTesterWidget = ({
  width,
  height,
}: InitialResultByTesterWidgetProps): JSX.Element => {
  const thisYear = new Date().getFullYear();
  const dateRange = { between: [`${thisYear}-01-01`, `${thisYear}-12-31`] };

  const { data } = useQuery<TopCompaniesData>(APPROVAL_QUERY, {
    variables: {
      where: {
        dateTested: dateRange,
      },
      limit: 10000,
    },
    fetchPolicy: 'no-cache',
  });

  const indexed = useMemo<Record<string, CompanyGrouping> | undefined>(
    () =>
      data?.initialResultByTester.reduce(
        (companies, row) => {
          companies[row.testingCompanyId] = companies[row.testingCompanyId] ?? {
            companyId: row.testingCompanyId,
            companyName: row.testingCompanyName,
            tests: {
              TOTAL: 0,
              PASS: 0,
              FAIL: 0,
            },
            testers: {},
          };

          companies[row.testingCompanyId].testers[row.testerId] = companies[
            row.testingCompanyId
          ].testers[row.testerId] ?? {
            testerId: row.testerId,
            testerName: row.testerName,
            tests: {
              TOTAL: 0,
              PASS: 0,
              FAIL: 0,
            },
          };

          companies[row.testingCompanyId].tests.TOTAL += row.agg.backflowTests;
          companies[row.testingCompanyId].testers[row.testerId].tests.TOTAL +=
            row.agg.backflowTests;

          if (row.initialResult) {
            companies[row.testingCompanyId].tests[row.initialResult] +=
              row.agg.backflowTests;
            companies[row.testingCompanyId].testers[row.testerId].tests[
              row.initialResult
            ] += row.agg.backflowTests;
          }

          return companies;
        },
        {} as Record<string, CompanyGrouping>
      ),
    [data?.initialResultByTester]
  );

  const companies: CompanyGrouping[] | undefined = useMemo(() => {
    const companies = indexed
      ? Object.keys(indexed).map((companyId) => indexed[companyId])
      : undefined;

    companies?.sort((a, b) => {
      return b.tests.TOTAL - a.tests.TOTAL;
    });
    return companies;
  }, [indexed]);

  const max = useMemo(
    () =>
      companies
        ? Math.max(...companies.map((company) => company.tests.TOTAL))
        : 0,
    [companies]
  );

  const history = useHistory();
  const mode = useMode();
  const storeFilters = useStoreFilters<TestReportFilters>('testReports');

  const onClick = useCallback((row: CompanyGrouping) => {
    const filters: TestReportFilters = {
      stringSearch: row.companyName,
      dateStart: `${thisYear}-01-01`,
      dateEnd: `${thisYear}-12-31`,
      waterSupplierStatus: ['ALL'],
    };
    storeFilters(filters);
    history.push(`/${mode}/test-reports`);
  }, []);

  return (
    <>
      {companies && width && (
        <>
          <Flex flexDirection="column" flexGrow={1} overflowY="auto">
            {companies.map((row) => (
              <Flex
                height="20%"
                minHeight="35px"
                flexShrink={0}
                justifyContent="center"
                key={row.companyId}
                flexDirection="column"
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
                onClick={() => onClick(row)}
              >
                <Flex justifyContent="space-between" mb={1}>
                  <Text variant="body2">{row.companyName}</Text>
                  <Text variant="body2" pr={1} fontWeight="bold">
                    {row.tests.TOTAL}
                  </Text>
                </Flex>
                <Box
                  sx={{
                    borderRadius: 'circle',
                    height: 10,
                    bg: 'statusPrimary',
                    width: (row.tests.TOTAL / max) * 100 + '%',
                  }}
                />
                <Flex
                  sx={{
                    mt: '1px',
                    borderRadius: 'circle',
                    overflow: 'hidden',
                    height: 10,
                  }}
                >
                  <Box
                    bg="statusSuccess"
                    title={`${row.tests.PASS} passed (${
                      Math.round((row.tests.PASS / row.tests.TOTAL) * 100) + '%'
                    })`}
                    flexShrink={1}
                    flexGrow={1}
                    width={(row.tests.PASS / row.tests.TOTAL) * 100 + '%'}
                  />
                  <Box
                    bg="statusError"
                    title={`${row.tests.FAIL} failed (${
                      Math.round((row.tests.FAIL / row.tests.TOTAL) * 100) + '%'
                    })`}
                    flexShrink={1}
                    flexGrow={1}
                    width={(row.tests.FAIL / row.tests.TOTAL) * 100 + '%'}
                  />
                </Flex>
              </Flex>
            ))}
          </Flex>
        </>
      )}
    </>
  );
};

export default InitialResultByTesterWidget;
